import { Search, X, Plus } from "lucide-react";

export function StatCard({ icon: Icon, iconBg, label, value, onClick, active }) {
  return (
    <div
      className={`rounded-2xl p-3 md:p-5 shadow-sm border flex items-center gap-3 md:gap-4 transition-all ${
        active
          ? "bg-white border-blue-400 ring-2 ring-blue-200 ring-offset-1 scale-[1.03]"
          : "bg-white border-gray-100"
      } ${onClick ? "cursor-pointer hover:shadow-md" : ""} ${onClick && !active ? "hover:border-blue-200" : ""}`}
      onClick={onClick}
    >
      <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
        <Icon size={20} className="md:w-[22px] md:h-[22px]" />
      </div>
      <div className="min-w-0">
        <p className="text-xs md:text-sm truncate text-slate-500">{label}</p>
        <p className="text-xl md:text-2xl font-bold text-slate-800">{value}</p>
      </div>
    </div>
  );
}

const badgeStyles = {
  "En ligne": "bg-green-100 text-green-700 border border-green-200",
  "Hors ligne": "bg-red-100 text-red-600 border border-red-200",
  "Actif": "bg-green-100 text-green-700",
  "Inactif": "bg-red-100 text-red-600 border border-red-200",
  "En cours": "bg-blue-100 text-blue-700 border border-blue-200",
  "Terminé": "bg-green-100 text-green-700 border border-green-200",
  "En attente": "bg-gray-100 text-gray-600 border border-gray-200",
  "Entreprise": "bg-gray-100 text-gray-600",
  "Institution": "bg-purple-100 text-purple-600",
};

export function Badge({ label }) {
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${badgeStyles[label] || "bg-gray-100 text-gray-600"}`}>
      {label}
    </span>
  );
}

export function UptimeBar({ value }) {
  const color = value >= 80 ? "bg-blue-500" : value >= 50 ? "bg-yellow-400" : "bg-red-400";
  return (
    <div>
      <p className="text-sm font-semibold text-slate-700 mb-1">{value}%</p>
      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = "Rechercher..." }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
    </div>
  );
}

export function Button({ children, onClick, variant = "primary", size = "md", className = "" }) {
  const base = "font-medium rounded-xl transition-colors flex items-center gap-2";
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
    secondary: "bg-white border border-gray-200 hover:bg-gray-50 text-slate-700",
    danger: "bg-red-600 hover:bg-red-700 text-white",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2.5 text-sm",
    lg: "px-6 py-3 text-base",
  };
  return (
    <button onClick={onClick} className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}>
      {children}
    </button>
  );
}

export function Modal({ isOpen, onClose, title, subtitle, children, size = "md" }) {
  if (!isOpen) return null;
  const sizes = { sm: "max-w-lg", md: "max-w-2xl", lg: "max-w-4xl", xl: "max-w-5xl", "2xl": "max-w-7xl" };
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end md:items-center justify-center md:p-4">
      <div className={`bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full ${sizes[size] || sizes.md} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100">
          <div>
            <h2 className="text-base md:text-lg font-bold text-slate-800">{title}</h2>
            {subtitle && <p className="text-slate-500 text-xs md:text-sm mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="p-4 md:p-6">{children}</div>
      </div>
    </div>
  );
}

export function FormField({ label, required, error, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

export function Input({ placeholder, value, onChange, type = "text", className = "" }) {
  return (
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 ${className}`}
    />
  );
}

export function Textarea({ placeholder, value, onChange, rows = 4 }) {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      rows={rows}
      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 resize-none"
    />
  );
}

export function Table({ headers, children }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="border-b border-gray-100">
            {headers.map((h) => (
              <th key={h} className="text-left px-3 md:px-6 py-3 md:py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">{children}</tbody>
      </table>
    </div>
  );
}

export function ActionMenu() {
  return (
    <button className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="8" cy="3" r="1.5"/>
        <circle cx="8" cy="8" r="1.5"/>
        <circle cx="8" cy="13" r="1.5"/>
      </svg>
    </button>
  );
}